//
// Copyright (c) 2011-2019 by Curalate, Inc.
//
import { PojoSet } from 'pojo-sets';

import ClientId from '@models/ClientId';

export interface IScheduleValidationRequest {
  catalogUrl: string;
  languageCode: CATALOG_LANGUAGE_CODES;
  countryCode: CATALOG_COUNTRY_CODES;
  clientId: ClientId;
  login?: string;
  password?: string;
}

export interface IValidateCatalogSourceRequest {
  location: string;
  login?: string;
  password?: string;
}

export interface IValidateCatalogSourceResponse {
  succeeded: boolean;
  failureReason?: CATALOG_SOURCE_ERROR;
}

export enum CATALOG_LANGUAGE_CODES {
  'en' = 0,
  'fr' = 1,
  'de' = 2,
  'it' = 3,
  'es' = 4,
  'nl' = 5,
}

export enum CATALOG_COUNTRY_CODES {
  'US' = 0,
  'CA' = 1,
  'GB' = 2,
  'FR' = 3,
  'DE' = 4,
  'AU' = 5,
  'IT' = 6,
  'ES' = 7,
  'NL' = 8,
  'IE' = 9,
}

export interface IScheduleValidationResponse {
  token: string;
}

export interface IUploadFileResponse {
  s3path: URL;
  name: string;
}

export interface IValidationStatusResponse {
  status: VALIDATION_STATUS;
}

export enum VALIDATION_STATUS {
  PENDING = 'pending',
  DOWNLOADING = 'downloading',
  FORMAT_CHECK = 'format_check',
  PROCESSING = 'processing',
  COMPLETE = 'complete',

  FAILED_DOWNLOAD = 'failed_download',
  FAILED_DOWNLOAD_TIMEOUT = 'failed_download_timeout',
  FAILED_FILE_PARSE = 'failed_file_parse',
  FAILED_UNKNOWN = 'failed_unknown',
}

export enum CATALOG_SOURCE_ERROR {
  INVALID_FEED_CREDENTIALS = 'INVALID_FEED_CREDENTIALS',
  INVALID_FEED_LOCATION = 'INVALID_FEED_LOCATION',
  INVALID_FEED_PROTOCOL = 'INVALID_FEED_PROTOCOL',
}

export const VALIDATING_STATE = PojoSet.from([
  VALIDATION_STATUS.PENDING,
  VALIDATION_STATUS.DOWNLOADING,
  VALIDATION_STATUS.FORMAT_CHECK,
  VALIDATION_STATUS.PROCESSING,
]);

export const VALIDATION_FAILED_STATE = PojoSet.from([
  VALIDATION_STATUS.FAILED_DOWNLOAD,
  VALIDATION_STATUS.FAILED_DOWNLOAD_TIMEOUT,
  VALIDATION_STATUS.FAILED_FILE_PARSE,
  VALIDATION_STATUS.FAILED_UNKNOWN,
]);

export interface IFeedValidationSummaryResponse {
  feedValidation: IFeedValidationSummaryContent;
}

interface IFeedValidationSummaryContent {
  token: string;
  status: string;
  feedLocation: IFeedLocation;
  locale: {
    languageCode: CATALOG_LANGUAGE_CODES,
    countryCode: CATALOG_COUNTRY_CODES,
  };
  clientId: string;
  createdTimestamp: number;
  summary: IValidationSummary;
  feedInfo?: {
    totalRows: number;
    hasItemGroupId: boolean;
  };
  errorFileLocation?: {
    S3FileLocation: {
      bucket: string;
      key: string;
    }
  };
}

interface IFeedLocation {
  protocol: string;
  uri: string;
  contentType?: string;
  credentials?: {
    login: string,
    password: string,
  };
  catalogId?: number;
}

interface IValidationSummary {
  processedRows: number;
  successfulRows: number;
  failedRows: number;
  errorCounts: Map<FeedValidationError, number>;
}

export enum FeedValidationError {
  DUPLICATE_ID = 'DUPLICATE_ID',
  MALFORMED_ID = 'MALFORMED_ID',
  MALFORMED_LINK = 'MALFORMED_LINK',
  MALFORMED_TITLE = 'MALFORMED_TITLE',
  MALFORMED_IMAGE_LINK = 'MALFORMED_IMAGE_LINK',
  MISSING_ID = 'MISSING_ID',
  MISSING_LINK = 'MISSING_LINK',
  MISSING_TITLE = 'MISSING_TITLE',
  MISSING_IMAGE_LINK = 'MISSING_IMAGE_LINK',
  UNPARSEABLE = 'UNPARSEABLE',
  UNREACHABLE_LINK = 'UNREACHABLE_LINK',
  UNREACHABLE_IMAGE_LINK = 'UNREACHABLE_IMAGE_LINK',
}

export interface IValidSampleResponse {
  rows: IValidSampleContent[];
}

export interface IValidSampleContent {
  rowNumber: number;
  feedId: string;
  title: string;
  link: string;
  imageLink: string;
  imageFileId: string;
  price: {
    price: number;
    currencyCode: string;
  };
  category: string;
  groupId: string;
  color: string;
}

export interface IInvalidSampleResponse {
  sample: Partial<Record<FeedValidationError, IValidationResultsContent[]>>;
}

export interface IValidationResultsResponse {
  results: IValidationResultsContent[];
  summary: IValidationSummary;
}

export interface IValidationResultsContent {
  rowNumber: number;
  successful?: boolean;
  failureReasons?: FeedValidationError[];
  feedId?: string;
  title?: string;
  link?: string;
  imageLink?: string;
  imageFileId?: string;
  price?: {
    price: number;
    currencyCode: string;
  };
  category?: string;
  groupId?: string;
  color?: string;
}
